import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

/**
 * Keyboard interaction for a single-select vertical listbox, per the WAI-ARIA listbox pattern,
 * implemented with a roving tabindex.
 *
 * Background: the assignment centre's two selector panels started life as `<div onClick>` with no
 * keyboard path at all. They were then given `role="listbox"` / `role="option"` and an
 * Enter/Space handler — which reads as fixed but is not: an option that announces itself as an
 * option and then ignores every arrow key is a listbox in name only. Arrow/Home/End left focus
 * stranded on whichever option Tab happened to land on.
 *
 * What this provides:
 *   - ArrowDown / ArrowUp move the active option (clamped at the ends; the WAI-ARIA listbox
 *     pattern does not wrap, and wrapping past the last item hides the list's extent).
 *   - Home / End jump to the first / last option.
 *   - Enter / Space select the focused option.
 *   - Exactly one option is in the tab order at a time (`tabIndex` 0 vs -1); Tab therefore moves
 *     past the whole listbox rather than through every row.
 *
 * RTL: this is a VERTICAL list, so its axis is Up/Down in both directions — arrow semantics mirror
 * only for horizontally laid-out widgets. ArrowLeft/ArrowRight are deliberately left UNBOUND: any
 * binding for them would be wrong in at least one writing direction, and leaving them unbound lets
 * them keep their normal meaning (caret movement / horizontal scroll).
 *
 * Selection does NOT follow focus. Selecting here swaps the entire adjacent detail panel and
 * triggers a fetch, which the pattern calls out as too expensive to fire on every arrow press.
 */
export interface ListboxOptionProps {
  tabIndex: number;
  ref: (element: HTMLDivElement | null) => void;
  onFocus: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export interface UseListboxKeyboardResult {
  /** Index of the option that currently owns the tab stop. */
  activeIndex: number;
  /** Spread onto each `role="option"` element, in render order. */
  getOptionProps: (index: number) => ListboxOptionProps;
}

export function useListboxKeyboard(
  optionCount: number,
  onSelect: (index: number) => void,
  initialIndex = 0,
): UseListboxKeyboardResult {
  const [activeIndex, setActiveIndex] = useState(() => (initialIndex > 0 ? initialIndex : 0));
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Both lists are filtered live. If the list shrinks below the active index, every option would
  // render `tabIndex={-1}` and the listbox would drop out of the tab order entirely — the same
  // quiet-degradation shape as the rest of this wave, so clamp instead of letting it happen.
  useEffect(() => {
    optionRefs.current.length = optionCount;
    setActiveIndex((current) => {
      if (optionCount === 0) return 0;
      return current > optionCount - 1 ? optionCount - 1 : current;
    });
  }, [optionCount]);

  const moveTo = useCallback((index: number) => {
    setActiveIndex(index);
    optionRefs.current[index]?.focus();
  }, []);

  const getOptionProps = useCallback(
    (index: number): ListboxOptionProps => ({
      tabIndex: index === activeIndex ? 0 : -1,
      ref: (element: HTMLDivElement | null) => {
        optionRefs.current[index] = element;
      },
      // Pointer focus and Tab focus must keep the roving tab stop in sync, otherwise the next
      // arrow press would jump from a stale position.
      onFocus: () => setActiveIndex(index),
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (optionCount === 0) return;
        let next: number;
        switch (event.key) {
          case 'ArrowDown':
            next = index < optionCount - 1 ? index + 1 : index;
            break;
          case 'ArrowUp':
            next = index > 0 ? index - 1 : index;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = optionCount - 1;
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            onSelect(index);
            return;
          default:
            return;
        }
        // Stop the arrow/Home/End keys from scrolling the page out from under the list.
        event.preventDefault();
        moveTo(next);
      },
    }),
    [activeIndex, moveTo, onSelect, optionCount],
  );

  return { activeIndex, getOptionProps };
}

export default useListboxKeyboard;
